/**
 * A/B tests (Pro `abTests`), buyer side: the visitor id.
 *
 * One random id per browser (`pf_ab`), kept in a FIRST-PARTY cookie on the
 * creator's own store host (1 year, SameSite=Lax) and mirrored in
 * localStorage so either copy restores the other. The API hashes it with the
 * test id to pick the visitor's variant, so the same visitor always sees the
 * same price / headline / cover / checkout layout — on the product page, the
 * checkout and every reload. It is not a fingerprint: nothing about the
 * device is read, and it carries no personal data.
 *
 * Every checkout request sends it as `abVisitor`; the server decides the
 * variant (and the price) from its own record of that id.
 *
 * Safe without a window (SSR / tests) and when storage is blocked: the id
 * then lives in memory for the page's lifetime.
 */

export const AB_VISITOR_COOKIE = "pf_ab";
export const AB_VISITOR_STORAGE_KEY = "pf-ab-visitor";
const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;
const VALID = /^[A-Za-z0-9_-]{16,64}$/;

let memoryId: string | null = null;

export const isAbVisitorId = (value: unknown): value is string =>
	typeof value === "string" && VALID.test(value);

const randomId = (): string => {
	const bytes = new Uint8Array(16);
	const cryptoApi = globalThis.crypto;
	if (cryptoApi?.getRandomValues) {
		cryptoApi.getRandomValues(bytes);
	} else {
		for (let i = 0; i < bytes.length; i++) {
			bytes[i] = Math.floor(Math.random() * 256);
		}
	}
	return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

const readCookie = (doc: Document): string | null => {
	try {
		for (const part of doc.cookie.split(";")) {
			const [name, ...rest] = part.trim().split("=");
			if (name === AB_VISITOR_COOKIE) {
				const value = decodeURIComponent(rest.join("="));
				return isAbVisitorId(value) ? value : null;
			}
		}
	} catch {
		// Cookies blocked.
	}
	return null;
};

const readStored = (win: Window): string | null => {
	try {
		const value = win.localStorage?.getItem(AB_VISITOR_STORAGE_KEY);
		return isAbVisitorId(value) ? value : null;
	} catch {
		return null;
	}
};

const persist = (win: Window, id: string) => {
	try {
		const secure = win.location?.protocol === "https:" ? "; Secure" : "";
		win.document.cookie = `${AB_VISITOR_COOKIE}=${id}; Max-Age=${ONE_YEAR_SECONDS}; Path=/; SameSite=Lax${secure}`;
	} catch {
		// Cookies blocked: localStorage / memory still hold it.
	}
	try {
		win.localStorage?.setItem(AB_VISITOR_STORAGE_KEY, id);
	} catch {
		// Storage blocked.
	}
};

/**
 * The visitor's id, minted on first use. Re-writes the cookie when only the
 * localStorage copy survived (and vice versa). Never throws.
 */
export function getAbVisitorId(
	win: Window | undefined = typeof window === "undefined" ? undefined : window,
): string | null {
	if (!win) return memoryId;
	const fromCookie = readCookie(win.document);
	const fromStorage = readStored(win);
	const id = fromCookie ?? fromStorage ?? memoryId ?? randomId();
	if (id !== fromCookie || id !== fromStorage) persist(win, id);
	memoryId = id;
	return id;
}

/** The id only if this browser already has one (no minting). */
export function readAbVisitorId(
	win: Window | undefined = typeof window === "undefined" ? undefined : window,
): string | null {
	if (!win) return memoryId;
	return readCookie(win.document) ?? readStored(win) ?? memoryId;
}

/** `{ abVisitor }` for a checkout request body (empty when unavailable). */
export function abVisitorBody(): { abVisitor?: string } {
	const id = getAbVisitorId();
	return id ? { abVisitor: id } : {};
}

// ── The variant the page rendered ───────────────────────────────────────────
//
// The buyer must never pay a price other than the one they saw. Every
// checkout request (and the price quote / personal offer lookups) therefore
// sends the variant id the page ACTUALLY rendered (`abVariant`), and the
// server applies the variant only when that matches its own record of the
// visitor. "" = the page showed the product as it is (no test, or its
// assignment lookup failed): the server then charges the product's own price
// and leaves the checkout out of the test.
//
// Kept per product, in memory and in localStorage (the early checkout session
// starts before the app boots and reads it from there).

export const AB_RENDERED_STORAGE_KEY = "pf-ab-rendered";
const MAX_RENDERED = 50;
const renderedMemory = new Map<string, string>();

const readRenderedMap = (win: Window | undefined): Record<string, string> => {
	try {
		const raw = win?.localStorage?.getItem(AB_RENDERED_STORAGE_KEY);
		const parsed = raw ? JSON.parse(raw) : null;
		return parsed && typeof parsed === "object" ? parsed : {};
	} catch {
		return {};
	}
};

/** Record what the page rendered for `productId` (null = the product as is). */
export function setRenderedAbVariant(
	productId: unknown,
	variantId: string | null | undefined,
	win: Window | undefined = typeof window === "undefined" ? undefined : window,
): void {
	if (typeof productId !== "string" || !productId) return;
	const value = typeof variantId === "string" ? variantId : "";
	renderedMemory.set(productId, value);
	try {
		const map = readRenderedMap(win);
		delete map[productId];
		map[productId] = value;
		const keys = Object.keys(map);
		for (const key of keys.slice(0, Math.max(0, keys.length - MAX_RENDERED))) {
			delete map[key];
		}
		win?.localStorage?.setItem(AB_RENDERED_STORAGE_KEY, JSON.stringify(map));
	} catch {
		// Storage blocked: memory still holds it for this page.
	}
}

/** The variant id the page rendered for `productId`, or "" for none. */
export function renderedAbVariant(
	productId: unknown,
	win: Window | undefined = typeof window === "undefined" ? undefined : window,
): string {
	if (typeof productId !== "string" || !productId) return "";
	const fromMemory = renderedMemory.get(productId);
	if (fromMemory !== undefined) return fromMemory;
	const stored = readRenderedMap(win)[productId];
	return typeof stored === "string" ? stored : "";
}

/** Test hook. */
export const resetAbVisitorMemory = () => {
	memoryId = null;
	renderedMemory.clear();
};
